import { Link } from '@tanstack/react-router'
import type { GridApi, ICellRendererParams } from 'ag-grid-community'
import { useCallback, useMemo, useRef, useState } from 'react'

import { Badge } from '@/components/base/badge'
import { Checkbox } from '@/components/base/checkbox'
import { ContainerHeader } from '@/components/base/container'
import { Input } from '@/components/base/input'
import { Text } from '@/components/base/text'
import { FilterButton } from '@/components/table/filter-button'
import { Pagination } from '@/components/table/pagination'
import { Table, TableColDef } from '@/components/table/table'
import { useI18n } from '@/i18n/i18n-react'

import {
	GroupMemberFilters,
	GroupMembersAdvancedFilterDrawer,
	UNASSIGNED_GROUP_FILTER
} from './-group-members-advanced-filter-drawer'

const PAGE_SIZE = 10

type MemberItem = {
	uuid: string
	name: string
	group?: { uuid: string; name: string }
}

type GroupOption = {
	uuid: string
	name: string
}

// Router link target; `to` is a route path string spread into <Link> (typed
// loosely here, like the shared Table does, since it comes from typed callers).
type MemberLink = {
	to: string
	params: Record<string, string>
}

export type GroupMembersTableLabels = {
	searchPlaceholder: string
	advancedFilters: string
	assignedColumn: string
	nameColumn: string
	groupColumn: string
	unassignedLabel: string
	empty: string
}

type GroupMembersTableProps = {
	title: string
	items: MemberItem[]
	groups: GroupOption[]
	value: string[]
	onChange: (next: string[]) => void
	itemLink: (uuid: string) => MemberLink
	groupLink: (uuid: string) => MemberLink
	labels: GroupMembersTableLabels
}

export function GroupMembersTable({
	title,
	items,
	groups,
	value,
	onChange,
	itemLink,
	groupLink,
	labels
}: GroupMembersTableProps) {
	const { locale } = useI18n()

	const [q, setQ] = useState('')
	const [offset, setOffset] = useState(0)
	const [filters, setFilters] = useState<GroupMemberFilters>({ groups: [] })
	const [filtersOpen, setFiltersOpen] = useState(false)

	const gridApiRef = useRef<GridApi<MemberItem> | null>(null)

	// Membership lives in the form field (`value`). Keep a ref to the latest set
	// so the checkbox cell renderer always reads current state without recreating
	// the column defs on every toggle.
	const selected = useMemo(() => new Set(value), [value])
	const selectedRef = useRef(selected)
	selectedRef.current = selected

	// Snapshot of the initially-assigned members, captured once. Sorting uses this
	// (not the live selection) so toggling a checkbox never re-sorts / jumps the
	// row, mirroring the pricing grid convention.
	const initialAssignedRef = useRef<Set<string>>(new Set(value))

	const toggle = useCallback(
		(uuid: string) => {
			const next = new Set(selectedRef.current)
			if (next.has(uuid)) {
				next.delete(uuid)
			} else {
				next.add(uuid)
			}
			selectedRef.current = next
			onChange([...next])
			gridApiRef.current?.refreshCells({ force: true, columns: ['assign'] })
		},
		[onChange]
	)

	// Assigned-first, then alphabetical by name, then uuid tiebreak.
	const orderedItems = useMemo(() => {
		const initial = initialAssignedRef.current
		return [...items].sort((a, b) => {
			const aAssigned = initial.has(a.uuid)
			const bAssigned = initial.has(b.uuid)
			if (aAssigned !== bAssigned) {
				return aAssigned ? -1 : 1
			}
			const byName = a.name.localeCompare(b.name, locale)
			if (byName !== 0) {
				return byName
			}
			return a.uuid.localeCompare(b.uuid)
		})
	}, [items, locale])

	const filtered = useMemo(() => {
		const query = q.trim().toLowerCase()
		return orderedItems.filter(item => {
			const matchesQuery = query === '' || item.name.toLowerCase().includes(query)
			if (!matchesQuery) {
				return false
			}
			if (filters.groups.length === 0) {
				return true
			}
			if (!item.group) {
				return filters.groups.includes(UNASSIGNED_GROUP_FILTER)
			}
			return filters.groups.includes(item.group.uuid)
		})
	}, [orderedItems, q, filters])

	const pageItems = useMemo(() => filtered.slice(offset, offset + PAGE_SIZE), [filtered, offset])

	const columnDefs = useMemo<TableColDef<MemberItem>[]>(
		() => [
			{
				colId: 'assign',
				headerName: labels.assignedColumn,
				width: 100,
				flex: 0,
				sortable: false,
				resizable: false,
				suppressMovable: true,
				cellRenderer: (params: ICellRendererParams<MemberItem>) => {
					const uuid = params.data?.uuid
					if (!uuid) {
						return null
					}
					return (
						<div className='flex h-full items-center'>
							<Checkbox checked={selectedRef.current.has(uuid)} onCheckedChange={() => toggle(uuid)} />
						</div>
					)
				}
			},
			{
				colId: 'name',
				headerName: labels.nameColumn,
				field: 'name',
				minWidth: 160,
				flex: 2,
				sortable: false,
				cellRenderer: (params: ICellRendererParams<MemberItem>) => {
					const item = params.data
					if (!item) {
						return null
					}
					return (
						<Link {...(itemLink(item.uuid) as any)} className='hover:underline'>
							{item.name}
						</Link>
					)
				}
			},
			{
				colId: 'group',
				headerName: labels.groupColumn,
				minWidth: 160,
				flex: 1,
				sortable: false,
				cellRenderer: (params: ICellRendererParams<MemberItem>) => {
					const group = params.data?.group
					if (!group) {
						return <span className='text-ui-fg-subtle'>-</span>
					}
					return (
						<Link {...(groupLink(group.uuid) as any)}>
							<Badge size='2xsmall'>{group.name}</Badge>
						</Link>
					)
				}
			}
		],
		[labels, itemLink, groupLink, toggle]
	)

	const header = (
		<ContainerHeader
			heading={title}
			rightSide={
				items.length === 0 ? undefined : (
					<>
						<div className='w-auto'>
							<Input
								size='small'
								type='search'
								value={q}
								placeholder={labels.searchPlaceholder}
								onChange={event => {
									setQ(event.target.value)
									setOffset(0)
								}}
							/>
						</div>
						<FilterButton onClick={() => setFiltersOpen(true)} activeCount={filters.groups.length} />
					</>
				)
			}
		/>
	)

	if (items.length === 0) {
		return (
			<>
				{header}
				<Text size='small' className='px-6 py-3 text-ui-fg-subtle'>
					{labels.empty}
				</Text>
			</>
		)
	}

	return (
		<>
			{header}
			<Table<MemberItem>
				data={pageItems}
				columnDefs={columnDefs}
				getRowId={row => row.uuid}
				suppressCellFocus
				domLayout='autoHeight'
				onGridReady={event => {
					gridApiRef.current = event.api
				}}
			/>
			<div className='px-6 py-4'>
				<Pagination totalCount={filtered.length} limit={PAGE_SIZE} offset={offset} onOffsetChange={setOffset} />
			</div>
			<GroupMembersAdvancedFilterDrawer
				open={filtersOpen}
				onOpenChange={setFiltersOpen}
				groups={groups}
				defaultValues={filters}
				onApply={next => {
					setFilters(next)
					setOffset(0)
				}}
				labels={{ groupLabel: labels.groupColumn, unassignedLabel: labels.unassignedLabel }}
			/>
		</>
	)
}
